<template>
  <div>
    <div class="d-flex mb-2 justify-content-between">
      <!-- 过滤 -->
      <div class="d-flex align-items-center">
        <a-radio-group v-model="dateMode">
          <a-radio-button v-for="item in timeOpts" :key="item.key" :value="item.key">{{ item.label }}</a-radio-button>
          <custom-date :customDate.sync="customDate" :time.sync="dateMode" :customTimeLabel="customTimeLabel" canSelectTodayAfter />
        </a-radio-group>
      </div>
      <!-- 导出 -->
      <div class="d-flex">
        <a-select v-model="predictGroupType" class="mr-2">
          <a-select-option value="day">{{this.$t('bill.show_group_day')}}</a-select-option>
          <a-select-option value="month">{{this.$t('bill.show_group_month')}}</a-select-option>
        </a-select>
        <a-tooltip :title="$t('common.export_excel')">
          <a-button class="mr-2" @click="exportExcel"><icon type="excel" /></a-button>
        </a-tooltip>
        <a-tooltip :title="$t('common.export_pdf')">
          <a-button @click="exportPdf"><icon type="pdf" /></a-button>
        </a-tooltip>
      </div>
    </div>
    <div id="predict-target">
      <div class="mt-2 mb-2" v-if="showPredictTotal">
        <span>{{this.$t('bill.predict_total')}}：<span style="font-weight: 600; color: #4FA1F8; font-size: 18px;">{{ currencySign }} {{ predictTotal }}</span></span>
      </div>
      <div class="mt-3 mb-2" v-if="diffText">
        {{diffText}} <help-tooltip :text="$t('bill.predict.diff_tip')" />
      </div>
      <!-- 图 -->
      <bill-card :header="header">
        <a-card :style="`min-height:${chartMinHeight};`">
          <e-chart :options="chartOptions" style="width:100%;height:202px" autoresize />
        </a-card>
      </bill-card>
    </div>
    <!-- 表 -->
    <vxe-grid
      class="w-100 mt-2"
      border
      :columns="columns"
      :data="tableDataList"
      show-header-overflow
      auto-resize
      :pager-config="tablePage"
      :sort-config="{sortMethod: sortMethod}"
      @page-change="handlePageChange"
      @cell-click="handleCellClick" />
  </div>
</template>

<script>
import XLSX from 'xlsx'
import currencyRequest from '@Bill/mixins/currencyRequest'
import { getPdf } from '@/utils/echart'
import { addDataToSheetAfterFormat } from '@/utils/xlsx'
import { numerify } from '@/filters'
import CustomDate from '@/components/DateTime/CustomDate'

export default {
  components: {
    CustomDate,
  },
  mixins: [currencyRequest],
  props: {
    getParams: Object,
    defaultDateMode: String,
    defaultCustomDate: Object,
  },
  data () {
    return {
      header: this.$t('bill.predict_data'),
      chartMinHeight: '202px',
      dateMode: this.getParams ? this.defaultDateMode || '30day_near' : '30day',
      customDate: this.defaultCustomDate || {},
      seriesArr: [],
      tablePage: {
        currentPage: 1,
        pageSize: 10,
        total: 0,
      },
      customTimeLabel: '',
      showPredictTotal: !this.getParams,
      showAmount: false,
      diffence: '',
      predictGroupType: 'day',
    }
  },
  computed: {
    columns () {
      const ret = [
        {
          field: 'time',
          title: this.$t('bill.date'),
        },
        {
          field: 'predict',
          title: this.$t('bill.predict_data'),
          formatter: ({ row }) => numerify(row.predict, '0,0.00'),
        },
      ]
      if (this.showAmount) {
        ret.push({
          field: 'amount',
          title: this.$t('bill.actual_cost'),
          formatter: ({ row }) => numerify(row.amount, '0,0.00'),
        })
      }
      return ret
    },
    timeOpts () {
      if (this.getParams) {
        return [
          { key: '30day_near', label: this.$t('bill.predict.near_30day') },
          { key: '90day_near', label: this.$t('bill.predict.near_90day') },
          { key: '6month', label: this.$t('bill.predict.future_6month') },
          { key: '1year', label: this.$t('bill.predict.future_1year') },
          { key: '2year', label: this.$t('bill.predict.future_2year') },
          { key: '5year', label: this.$t('bill.predict.future_5year') },
        ]
      } else {
        return [
          { key: '30day', label: this.$t('bill.predict.future_30day') },
          { key: '90day', label: this.$t('bill.predict.future_90day') },
          { key: '6month', label: this.$t('bill.predict.future_6month') },
          { key: '1year', label: this.$t('bill.predict.future_1year') },
          { key: '2year', label: this.$t('bill.predict.future_2year') },
          { key: '5year', label: this.$t('bill.predict.future_5year') },
        ]
      }
    },
    chartOptions () {
      const legendItems = this.showAmount ? ['amount', 'predict'] : ['predict']
      const series = legendItems.map(key => {
        const data = []
        this.seriesArr.map(item => {
          data.push(item[key])
        })
        return {
          name: key,
          type: 'bar',
          data,
          z: 0,
          barGap: '0',
        }
      })
      const xAxisData = this.seriesArr.map(item => item.time)
      return {
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true,
        },
        title: {
          show: true,
          text: this.header,
          textStyle: {
            fontSize: '14px',
            fontWeight: 'normal',
          },
        },
        legend: {
          show: true,
          width: '75%',
          type: 'scroll',
          data: legendItems,
          formatter: (name) => {
            return name === 'amount' ? this.$t('bill.actual_cost') : this.$t('bill.predict')
          },
        },
        color: ['#ADD1F3', '#F3CBAD'],
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow',
            shadowStyle: {
              color: 'rgb(77, 161, 255)',
              opacity: 0.1,
            },
          },
          position: (point, params, dom, rect, size) => {
            dom.style.border = 'none'
            dom.style.backgroundColor = 'transparent'
            let series = params
            series.sort((a, b) => b.value - a.value)
            series = series.map((line, index) => {
              if (line.value === undefined || line.value === 0.0) {
                return ''
              }
              const name = line.seriesName === 'amount' ? this.$t('bill.actual_cost') : this.$t('bill.predict')
              return `<div style="color: #616161;">${line.marker} <span>${name}</span>:  <span>${this.currencySign} ${numerify(line.value, '0,0.00')}</span></div>`
            }).join('')
            if (series.length === 0) {
              dom.innerHTML = ''
              return
            }
            const wrapper = `<div class="chart-tooltip-wrapper">
                          <div style="color: #5D6F80;">${params[0].name}</div>
                          <div class="lines-wrapper">${series}</div>
                        </div>`
            dom.innerHTML = wrapper
          },
        },
        toolbox: {
          feature: {
            magicType: { show: true, type: ['line', 'bar'] },
          },
        },
        series: series,
        xAxis: {
          type: 'category',
          data: xAxisData,
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            formatter: (value) => {
              return `${this.currencySign} ${value}`
            },
          },
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
        },
        // dataZoom: {
        //   type: 'inside', /* 数据缩放 **/
        // },
        barMaxWidth: 30,
      }
    },
    tableDataList () {
      const { currentPage, pageSize } = this.tablePage
      return this.seriesArr.slice((currentPage - 1) * pageSize, currentPage * pageSize)
    },
    predictTotal () {
      const total = this.seriesArr.reduce((total, item) => {
        return total + item.predict
      }, 0)
      return numerify(total, '0,0.00')
    },
    diffText () {
      if (this.diffence) {
        return this.$t('bill.predict.diff_text', [this.diffence])
      }
      return ''
    },
  },
  watch: {
    dateMode: {
      immediate: true,
      handler (val) {
        this.updateCustomTimeLabel(val, this.customDate)
        this.fetchData()
      },
    },
    customDate: {
      deep: true,
      handler (val) {
        this.updateCustomTimeLabel(this.dateMode, val)
        this.fetchData()
      },
    },
    predictGroupType (val) {
      this.fetchData()
    },
  },
  created () {
    if (this.defaultDateMode === 'custom' && this.defaultCustomDate.start) {
      this.updateCustomTimeLabel(this.defaultDateMode, this.defaultCustomDate)
    }
  },
  methods: {
    genParams (dateMode) {
      let ret = {
        scope: this.$store.getters.scope,
      }
      // 过去
      if (this.dateMode === '30day_near' || dateMode === '30day_near') {
        ret.start_day = parseInt(this.$moment().subtract(30, 'day').format('YYYYMMDD'))
        ret.end_day = parseInt(this.$moment().subtract(1, 'day').format('YYYYMMDD'))
        ret['group.0'] = this.predictGroupType || 'day'
      } else if (this.dateMode === '90day_near') {
        ret.start_day = parseInt(this.$moment().subtract(90, 'day').format('YYYYMMDD'))
        ret.end_day = parseInt(this.$moment().subtract(1, 'day').format('YYYYMMDD'))
        ret['group.0'] = this.predictGroupType || 'month'
      } else if (this.dateMode === '30day') { // 将来
        ret.start_day = parseInt(this.$moment().add(1, 'day').format('YYYYMMDD'))
        ret.end_day = parseInt(this.$moment().add(30, 'day').format('YYYYMMDD'))
        ret['group.0'] = this.predictGroupType || 'day'
      } else if (this.dateMode === '90day') {
        ret.start_day = parseInt(this.$moment().add(1, 'day').format('YYYYMMDD'))
        ret.end_day = parseInt(this.$moment().add(90, 'day').format('YYYYMMDD'))
        ret['group.0'] = this.predictGroupType || 'month'
      } else if (this.dateMode === '6month') {
        ret.start_day = parseInt(this.$moment().add(1, 'month').startOf('month').format('YYYYMMDD'))
        ret.end_day = parseInt(this.$moment().add(6, 'month').endOf('month').format('YYYYMMDD'))
        ret['group.0'] = this.predictGroupType || 'month'
      } else if (this.dateMode === '1year') {
        ret.start_day = parseInt(this.$moment().add(1, 'year').startOf('year').format('YYYYMMDD'))
        ret.end_day = parseInt(this.$moment().add(1, 'year').endOf('year').format('YYYYMMDD'))
        ret['group.0'] = this.predictGroupType || 'month'
      } else if (this.dateMode === '2year') {
        ret.start_day = parseInt(this.$moment().add(1, 'year').startOf('year').format('YYYYMMDD'))
        ret.end_day = parseInt(this.$moment().add(2, 'year').endOf('year').format('YYYYMMDD'))
        ret['group.0'] = this.predictGroupType || 'month'
      } else if (this.dateMode === '5year') {
        ret.start_day = parseInt(this.$moment().add(1, 'year').startOf('year').format('YYYYMMDD'))
        ret.end_day = parseInt(this.$moment().add(5, 'year').endOf('year').format('YYYYMMDD'))
        ret['group.0'] = this.predictGroupType || 'month'
      } else if (this.dateMode === 'custom') {
        const { start, end } = this.customDate
        ret.start_day = parseInt(start.format('YYYYMMDD'))
        ret.end_day = parseInt(end.format('YYYYMMDD'))
        ret['group.0'] = this.predictGroupType || 'month'
        const diffMonth = this.$moment(end).add(1, 'seconds').diff(start, 'months') // 相差几个月
        if (diffMonth <= 1) ret['group.0'] = this.predictGroupType || 'day'
      }
      if (this.getParams) {
        ret = { ...ret, ...this.getParams }
      }
      return ret
    },
    handlePageChange (params) {
      const { $event, currentPage, pageSize } = params
      const { type } = $event
      if (type === 'size') {
        this.tablePage.pageSize = pageSize
        this.tablePage.currentPage = 1
      }
      if (type === 'current') {
        this.tablePage.currentPage = currentPage
      }
    },
    async fetchData () {
      try {
        const params = this.genParams()
        const res = await new this.$Manager('predictions/query', 'v1').list({ params })
        this.updateDiffence(res)
        const timeKey = params['group.0']
        await this.$nextTick()
        const data = (res.data.data || []).map(item => {
          return {
            amount: item.amount || 0,
            predict: item.prediction || 0,
            time: this.$moment(item[timeKey] + '').format(timeKey === 'day' ? 'YYYY-MM-DD' : 'YYYY-MM'),
            index: item.day || item.month,
            future: item.future,
          }
        })
        data.sort((a, b) => a.index - b.index)
        this.seriesArr = data
        this.tablePage.total = this.seriesArr.length
        if (data.length) {
          this.showAmount = !data[0].future
        }
      } catch (error) {
        throw error
      }
    },
    async updateDiffence (res) {
      if (this.diffence || !this.getParams) return
      let dataList = res.data?.data || []
      if (this.dateMode !== '30day_near') {
        const params = this.genParams('30day_near')
        const res = await new this.$Manager('predictions/query', 'v1').list({ params })
        dataList = res.data?.data || []
      }
      let diff = 0
      let count = 0
      dataList.map(item => {
        if (item.amount) {
          diff += Math.abs(((item.prediction || 0) / (item.amount || 0)) - 1)
          count++
        }
      })
      this.diffence = numerify(count ? diff / count : 0, '0.00%')
    },
    updateCustomTimeLabel (type, date) {
      try {
        if (type === 'custom') {
          const start = this.$moment(date.start).format('YYYY-MM-DD')
          const end = this.$moment(date.end).format('YYYY-MM-DD')
          this.customTimeLabel = ` (${start} - ${end})`
        } else {
          this.customTimeLabel = ''
        }
      } catch (err) {
        this.customTimeLabel = ''
      }
    },
    exportExcel () {
      const data = [
        [this.$t('bill.date'), this.$t('bill.predict_data')],
      ]
      if (this.showAmount) {
        data[0].push(this.$t('bill.actual_cost'))
      }
      this.seriesArr.map(item => {
        data.push(this.showAmount ? [item.time, numerify(item.predict, '0.00'), numerify(item.amount, '0.00')] : [item.time, numerify(item.predict, '0.00')])
      })
      const filename = `${this.$t('bill.predict_data')}.xlsx`
      const ws_name = 'Sheet1'
      const wb = XLSX.utils.book_new()
      const ws = addDataToSheetAfterFormat({ data })
      XLSX.utils.book_append_sheet(wb, ws, ws_name)
      XLSX.writeFile(wb, filename)
    },
    exportPdf () {
      getPdf({
        title: this.$t('bill.predict_data'),
        target: '#predict-target',
      })
    },
  },
}
</script>
